import { folderStillExists, takenExplorerPaths } from './clipboard'
import {
  isPathPrefix,
  leafSegment,
  relocateUnderPrefix,
  subtreeFolderPaths,
  type PathItem
} from './tree'

export const UNDO_STACK_LIMIT = 25

export const UNDO_REFUSED = {
  occupied: 'A folder or item already exists at that destination.',
  edited: (name: string) =>
    `"${name}" has been edited since it was created. Undo would discard that work.`,
  extraChildren: (name: string) =>
    `"${name}" now contains items that were not part of the original action.`
} as const

export interface CodeItem extends PathItem {
  readonly code?: string
  /** Dashboard DB-write tools. Other explorers leave this unset. */
  readonly writebackToolIds?: readonly string[]
}

export type ExplorerUndoEntry =
  | {
      op: 'folder-relocate'
      label: string
      folderId?: string
      atPath: string
      toPath: string
      toName?: string
    }
  | {
      op: 'dashboard-relocate'
      label: string
      id: string
      atPath: string
      toPath: string
      toName?: string
    }
  | {
      op: 'remove-dashboard'
      label: string
      id: string
      atPath: string
      expectCode: string
    }
  | {
      op: 'remove-folder'
      label: string
      atPath: string
      createdPaths: readonly string[]
    }
  | {
      op: 'restore-dashboard'
      label: string
      name: string
      path: string
      code: string
      writebackToolIds?: readonly string[]
      /** Ontology nodes the row was linked to, restored alongside it. */
      anchors?: readonly string[]
    }
  | {
      op: 'restore-subtree'
      label: string
      rootPath: string
      folders: readonly { name: string; path: string }[]
      dashboards: readonly {
        name: string
        path: string
        code: string
        writebackToolIds?: readonly string[]
      }[]
    }

export type UndoResolveResult =
  | { kind: 'apply'; entry: ExplorerUndoEntry }
  | { kind: 'stale' }
  | { kind: 'blocked'; reason: string }

export function subtreeOccupantPaths<F extends PathItem, I extends PathItem>(
  rootPath: string,
  folders: readonly F[],
  items: readonly I[]
): Set<string> {
  const paths = subtreeFolderPaths(rootPath, folders, items)
  for (const item of items) {
    if (item.path === rootPath || isPathPrefix(rootPath, item.path)) paths.add(item.path)
  }
  return paths
}

export function snapshotFolderDelete<F extends PathItem, I extends CodeItem>(
  rootPath: string,
  folders: readonly F[],
  items: readonly I[]
): {
  folders: { name: string; path: string }[]
  dashboards: { name: string; path: string; code: string; writebackToolIds?: string[] }[]
} {
  const folderRows = folders
    .filter((folder) => folder.path === rootPath || isPathPrefix(rootPath, folder.path))
    .map((folder) => ({ name: folder.name, path: folder.path }))
  const known = new Set(folderRows.map((folder) => folder.path))
  for (const path of subtreeFolderPaths(rootPath, folders, items)) {
    if (path === '' || known.has(path)) continue
    folderRows.push({ name: leafSegment(path), path })
    known.add(path)
  }
  const dashboards = items
    .filter((item) => isPathPrefix(rootPath, item.path) && item.path !== rootPath)
    .map((item) => ({
      name: item.name,
      path: item.path,
      code: item.code ?? '',
      ...(item.writebackToolIds && item.writebackToolIds.length > 0
        ? { writebackToolIds: [...item.writebackToolIds] }
        : {})
    }))
  return { folders: folderRows, dashboards }
}

export function relocatedCreatedPaths<F extends PathItem, I extends PathItem>(
  fromPath: string,
  toPath: string,
  folders: readonly F[],
  items: readonly I[]
): string[] {
  const paths = subtreeOccupantPaths(fromPath, folders, items)
  return [...paths].map((path) => relocateUnderPrefix(fromPath, toPath, path))
}

function pathIsFree(path: string, taken: ReadonlySet<string>, ignorePath?: string): boolean {
  if (path === ignorePath) return true
  return !taken.has(path)
}

export function resolveUndo<F extends PathItem, I extends CodeItem>(
  entry: ExplorerUndoEntry,
  folders: readonly F[],
  items: readonly I[]
): UndoResolveResult {
  const taken = takenExplorerPaths(folders, items)

  if (entry.op === 'folder-relocate') {
    if (!folderStillExists(entry.atPath, folders, items)) return { kind: 'stale' }
    if (!pathIsFree(entry.toPath, taken, entry.atPath)) {
      return { kind: 'blocked', reason: UNDO_REFUSED.occupied }
    }
    return { kind: 'apply', entry }
  }

  if (entry.op === 'dashboard-relocate') {
    const item = items.find((row) => row.id === entry.id)
    if (item === undefined || item.path !== entry.atPath) return { kind: 'stale' }
    if (!pathIsFree(entry.toPath, taken, entry.atPath)) {
      return { kind: 'blocked', reason: UNDO_REFUSED.occupied }
    }
    return { kind: 'apply', entry }
  }

  if (entry.op === 'remove-dashboard') {
    const item = items.find((row) => row.id === entry.id)
    if (item === undefined || item.path !== entry.atPath) return { kind: 'stale' }
    if ((item.code ?? '') !== entry.expectCode) {
      return { kind: 'blocked', reason: UNDO_REFUSED.edited(item.name) }
    }
    return { kind: 'apply', entry }
  }

  if (entry.op === 'remove-folder') {
    if (!folderStillExists(entry.atPath, folders, items)) return { kind: 'stale' }
    const allowed = new Set(entry.createdPaths)
    for (const path of subtreeOccupantPaths(entry.atPath, folders, items)) {
      if (!allowed.has(path)) {
        const folder = folders.find((row) => row.path === entry.atPath)
        return {
          kind: 'blocked',
          reason: UNDO_REFUSED.extraChildren(folder?.name ?? leafSegment(entry.atPath))
        }
      }
    }
    return { kind: 'apply', entry }
  }

  if (entry.op === 'restore-dashboard') {
    if (!pathIsFree(entry.path, taken)) {
      return { kind: 'blocked', reason: UNDO_REFUSED.occupied }
    }
    return { kind: 'apply', entry }
  }

  for (const folder of entry.folders) {
    if (!pathIsFree(folder.path, taken)) {
      return { kind: 'blocked', reason: UNDO_REFUSED.occupied }
    }
  }
  for (const dashboard of entry.dashboards) {
    if (!pathIsFree(dashboard.path, taken)) {
      return { kind: 'blocked', reason: UNDO_REFUSED.occupied }
    }
  }
  return { kind: 'apply', entry }
}
